import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { JsonViewer } from "@/components/common/JsonViewer";
import { Check, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import type { ResourceActionListItem } from "@/types/admin";
import { formatDateTime } from "@/lib/utils";

interface ResourceActionDetailDialogProps {
  action: ResourceActionListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Rendered in place of a value the row does not carry. */
const EMPTY = "—";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2 min-w-0 break-words">{value}</div>
    </div>
  );
}

function Mono({ value }: { value?: string | null }) {
  return value ? (
    <span className="font-mono text-xs select-all">{value}</span>
  ) : (
    <span className="text-muted-foreground">{EMPTY}</span>
  );
}

function CopyableMono({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context; the text stays selectable.
    }
  };

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Mono value={value} />
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied" : "Copy"}
        aria-label="Copy"
        className="shrink-0 cursor-pointer rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus:outline-none"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

/**
 * Parses a JSON column the API hands over as a string (`parameters`, `result`, `metadata`).
 *
 * Returns null for nothing to show — absent, blank, or the empty object the backend defaults to
 * — so the caller can hide the section rather than render "{}". Unparseable text comes back
 * as-is: showing the raw column beats swallowing it.
 */
function parseJsonColumn(value: string | null | undefined): unknown | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "{}" || trimmed === "null") return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

/**
 * Metadata keys the dialog draws in their own sections. The provisioning and reinstall jobs write
 * one row for the body posted to WiService and another for what came back, and the answer half also
 * carries the refusal when there was one — read as raw JSON, an operator has to know which key is
 * which half.
 */
const REQUEST_KEYS = [
  "apiEndpoint",
  "requestPayload",
  "requestSentAt",
] as const;

const RESPONSE_KEYS = [
  "executionId",
  "responsePayload",
  "responseReceivedAt",
  "responseAccepted",
  "providerEndpoint",
  "providerHttpStatus",
  "providerMessage",
  "providerErrorBody",
  "providerFailedAt",
] as const;

type JsonRecord = Record<string, unknown>;

/** The metadata document when it is a plain object; null for an array, a scalar, or raw text. */
function asRecord(value: unknown): JsonRecord | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as JsonRecord;
}

/** Whether a metadata value is worth drawing: JSON null and blank text read as absent. */
function present(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  return !(typeof value === "string" && value.trim() === "");
}

function asText(value: unknown): string | undefined {
  return present(value) ? String(value) : undefined;
}

/** A timestamp the jobs write in round-trip format; anything unparseable is shown as stored. */
function formatMetadataTime(value: unknown): string {
  const raw = asText(value);
  if (!raw) {
    return EMPTY;
  }

  return Number.isNaN(new Date(raw).getTime()) ? raw : formatDateTime(raw);
}

/**
 * The metadata left once the two provider sections have taken their keys — polling counters, clone
 * progress, whatever a later writer adds. Null when nothing is left, so the section stays hidden
 * rather than drawing an empty object.
 */
function remainingMetadata(source: JsonRecord): JsonRecord | null {
  const consumed = new Set<string>([...REQUEST_KEYS, ...RESPONSE_KEYS]);
  const rest: JsonRecord = {};

  for (const [key, value] of Object.entries(source)) {
    if (!consumed.has(key)) {
      rest[key] = value;
    }
  }

  return Object.keys(rest).length > 0 ? rest : null;
}

/**
 * Full view of one resource-action row. Everything shown here already lives on the list row —
 * unlike the audit log detail modal, there is no separate detail endpoint to fetch.
 */
export function ResourceActionDetailDialog({
  action,
  open,
  onOpenChange,
}: ResourceActionDetailDialogProps) {
  const navigate = useNavigate();

  const parameters = parseJsonColumn(action.parameters);
  const result = parseJsonColumn(action.result);
  const metadata = parseJsonColumn(action.metadata);

  // Raw text that failed to parse stays in the Metadata block below, untouched.
  const metadataRecord = asRecord(metadata);
  const hasRequest =
    metadataRecord !== null &&
    REQUEST_KEYS.some((key) => present(metadataRecord[key]));
  const hasResponse =
    metadataRecord !== null &&
    RESPONSE_KEYS.some((key) => present(metadataRecord[key]));
  const otherMetadata =
    metadataRecord === null ? metadata : remainingMetadata(metadataRecord);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Resource Action Details</DialogTitle>
        </DialogHeader>

        <DialogBody withoutFooter className="space-y-4">
          <DialogDescription>
            Complete information about this action
          </DialogDescription>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{action.type}</Badge>
            <Badge variant="outline">{action.status}</Badge>
            {action.isResourceDeleted && (
              <Badge
                variant="outline"
                className="border-destructive/30 bg-destructive-soft text-destructive-ink"
              >
                Resource deleted
              </Badge>
            )}
          </div>

          {action.description && (
            <p className="text-sm">{action.description}</p>
          )}

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Resource</div>
            <Field
              label="Name"
              value={
                action.isResourceDeleted ? (
                  action.resourceName
                ) : (
                  <button
                    onClick={() =>
                      navigate(`/admin/resources/${action.resourceId}`)
                    }
                    className="cursor-pointer text-left text-primary hover:underline"
                  >
                    {action.resourceName}
                  </button>
                )
              }
            />
            <Field label="Type" value={action.resourceType} />
            <Field
              label="Resource ID"
              value={<CopyableMono value={action.resourceId} />}
            />
            <Field
              label="VM UUID"
              value={
                action.vmUuid ? <CopyableMono value={action.vmUuid} /> : EMPTY
              }
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Actor</div>
            <Field label="User" value={action.userEmail || "System"} />
            <Field
              label="User ID"
              value={<CopyableMono value={action.userId} />}
            />
            <Field label="Tenant" value={action.tenantName} />
            <Field
              label="Tenant ID"
              value={<CopyableMono value={action.tenantId} />}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Timing</div>
            <Field
              label="Started"
              value={
                action.startedAt ? formatDateTime(action.startedAt) : EMPTY
              }
            />
            <Field
              label="Completed"
              value={
                action.completedAt ? formatDateTime(action.completedAt) : EMPTY
              }
            />
            <Field
              label="Duration"
              value={
                action.durationMs === null
                  ? EMPTY
                  : action.durationMs < 1000
                    ? `${action.durationMs} ms`
                    : `${(action.durationMs / 1000).toFixed(1)} s`
              }
            />
            <Field label="Created" value={formatDateTime(action.createdAt)} />
            <Field label="Updated" value={formatDateTime(action.updatedAt)} />
            <Field
              label="Action ID"
              value={<CopyableMono value={action.id} />}
            />
          </div>

          {action.errorMessage && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Error</div>
                <p className="text-sm text-destructive-ink">
                  {action.errorMessage}
                </p>
              </div>
            </>
          )}

          {hasRequest && metadataRecord && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Request to WiService</div>
                <Field
                  label="Endpoint"
                  value={<Mono value={asText(metadataRecord.apiEndpoint)} />}
                />
                <Field
                  label="Sent"
                  value={formatMetadataTime(metadataRecord.requestSentAt)}
                />
                {present(metadataRecord.requestPayload) && (
                  <JsonViewer
                    value={metadataRecord.requestPayload}
                    maxHeightClassName="max-h-60"
                  />
                )}
              </div>
            </>
          )}

          {hasResponse && metadataRecord && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Response from WiService
                </div>
                <Field
                  label="Received"
                  value={formatMetadataTime(
                    metadataRecord.responseReceivedAt ??
                      metadataRecord.providerFailedAt,
                  )}
                />
                {present(metadataRecord.responseAccepted) && (
                  <Field
                    label="Accepted"
                    value={
                      metadataRecord.responseAccepted ? (
                        "Yes"
                      ) : (
                        <span className="text-destructive-ink">No</span>
                      )
                    }
                  />
                )}
                {present(metadataRecord.executionId) && (
                  <Field
                    label="Execution ID"
                    value={
                      <CopyableMono
                        value={String(metadataRecord.executionId)}
                      />
                    }
                  />
                )}
                {present(metadataRecord.providerHttpStatus) && (
                  <Field
                    label="HTTP status"
                    value={String(metadataRecord.providerHttpStatus)}
                  />
                )}
                {present(metadataRecord.providerMessage) && (
                  <Field
                    label="Message"
                    value={
                      <span className="text-destructive-ink">
                        {String(metadataRecord.providerMessage)}
                      </span>
                    }
                  />
                )}
                {present(metadataRecord.responsePayload) && (
                  <JsonViewer
                    value={metadataRecord.responsePayload}
                    maxHeightClassName="max-h-60"
                  />
                )}
                {present(metadataRecord.providerErrorBody) && (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      Provider error body
                    </div>
                    <JsonViewer
                      value={metadataRecord.providerErrorBody}
                      maxHeightClassName="max-h-60"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Each JSON section is hidden when empty rather than showing "{}": most actions record
              parameters only, and a result only exists once the action has finished. */}
          {parameters !== null && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Parameters</div>
                <JsonViewer value={parameters} maxHeightClassName="max-h-60" />
              </div>
            </>
          )}

          {result !== null && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Result</div>
                <JsonViewer value={result} maxHeightClassName="max-h-60" />
              </div>
            </>
          )}

          {otherMetadata !== null && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {hasRequest || hasResponse ? "Other metadata" : "Metadata"}
                </div>
                <JsonViewer
                  value={otherMetadata}
                  maxHeightClassName="max-h-60"
                />
              </div>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
